import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useGetClaim, useGetProjects, getGetProjectsQueryKey, customInstance } from '@edison/api-client-react';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Printer,
  FileWarning,
  Lock,
  CircleDot,
  FileEdit,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { ReadinessRow, type ReadinessState } from '@/components/ui/readiness-row.js';
import { Card } from '@/components/ui/card.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Skeleton, SkeletonText } from '@/components/ui/skeleton.js';
import { InlineError } from '@/components/ui/inline-error.js';
import { ClaimModeBadge } from '@/components/claim-mode-badge.js';
import { EvidenceLog } from '@/pages/claims/evidence-log.js';
import { formatFy } from '@/lib/utils.js';

// ── Endpoint contracts (verified against artifacts/api-server/src/routes/claims.ts) ─────────
//   GET /api/claims/:id             → { data: Claim }              (via generated useGetClaim)
//   GET /api/claims/:id/readiness   → { data: { checks, blockingCount, readyToValidate, readyToLock, claimStatus } }
//   GET /api/claims/:id/expenditure → { data: { labour, nonLabour, totalExpenditure, asLodged, missingRates, mode } }
//
// Claims don't reference projects directly — they share an incomeYear (verified against
// artifacts/api-server/src/routes/projects.ts: GET /api/projects?incomeYear=&status= filters on
// exactly this). The per-project evidence log therefore lists active projects for the claim's
// incomeYear via the generated useGetProjects hook, which is complete (Project has id/name/status).

interface ReadinessCheck {
  code: string;
  status: 'ok' | 'warning' | 'blocking';
  message: string;
  detail?: string;
  state: ReadinessState;
}

interface ReadinessData {
  checks: ReadinessCheck[];
  blockingCount: number;
  readyToValidate: boolean;
  readyToLock: boolean;
  claimStatus: string;
}

interface ExpenditureData {
  labour?: {
    totalRdHours?: number;
    totalRdExpenditure?: number;
    approvedEntryCount?: number;
  };
  nonLabour?: {
    totalNonLabourExpenditure?: number;
  };
  totalExpenditure?: number;
  mode?: string;
}

const STATUS_ICON: Record<ReadinessCheck['status'], React.ReactNode> = {
  ok: <CheckCircle2 className="h-4 w-4" />,
  warning: <AlertTriangle className="h-4 w-4" />,
  blocking: <XCircle className="h-4 w-4" />,
};

// Claim lifecycle status → icon + human-readable label + badge variant.
// Status is NEVER conveyed by colour alone (§6.2) — every claim status carries both an icon and text.
const CLAIM_STATUS_META: Record<
  string,
  { label: string; icon: React.ReactNode; variant: 'default' | 'info' | 'positive' }
> = {
  draft: { label: 'Draft', icon: <FileEdit className="h-3.5 w-3.5" aria-hidden="true" />, variant: 'default' },
  validating: { label: 'Validating', icon: <CircleDot className="h-3.5 w-3.5" aria-hidden="true" />, variant: 'info' },
  locked: { label: 'Locked', icon: <Lock className="h-3.5 w-3.5" aria-hidden="true" />, variant: 'positive' },
};

// When printing, hide the whole app shell and everything except the summary document. Mirrors the
// working pattern in project-export/index.tsx. The `.no-print` class hides in-app controls (the
// Print button) so they never appear on the printed page / PDF.
const PRINT_STYLE = `@media print {
  body * { visibility: hidden !important; }
  #readiness-summary-doc, #readiness-summary-doc * { visibility: visible !important; }
  #readiness-summary-doc { position: absolute; left: 0; top: 0; width: 100%; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  #readiness-summary-doc .no-print, .no-print { display: none !important; }
  #readiness-summary-doc .summary-block { break-inside: avoid; }
}`;

function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(n);
}

// Collapsed-by-default wrapper around EvidenceLog — one per project contributing to the claim's
// expenditure. The project NAME must stay visible in print (an adviser needs to know which
// project each evidence section belongs to), so `no-print` lives ONLY on the chevron affordance,
// never on the button or the project name. The lazily-mounted EvidenceLog body stays
// expand-gated — the adviser expands what they want before printing.
function ProjectEvidenceSection({ projectId, projectName }: { projectId: string; projectName: string }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card variant="static" className="p-0 summary-block">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 p-4 text-left"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="flex items-center gap-2 text-sm font-medium text-ink">
          {expanded ? (
            <ChevronDown className="no-print h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          ) : (
            <ChevronRight className="no-print h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          )}
          Evidence log — {projectName}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-border/40 p-4">
          <EvidenceLog projectId={projectId} />
        </div>
      )}
    </Card>
  );
}

export function ClaimReadinessSummary({ claimId }: { claimId: string }) {
  // Claim entity fetched via the generated hook (its type is complete: name/incomeYear/status).
  const claimQuery = useGetClaim(claimId);

  const readinessQuery = useQuery<ReadinessData>({
    queryKey: ['claim-readiness', claimId],
    queryFn: async () => {
      const res = await customInstance<{ data: ReadinessData }>({
        url: `/api/claims/${claimId}/readiness`,
        method: 'GET',
      });
      return res.data;
    },
  });

  // generated GetClaimExpenditure type omits mode/asLodged (stale OpenAPI); call directly until codegen is regenerated
  const expenditureQuery = useQuery<ExpenditureData>({
    queryKey: ['claim-readiness-summary-expenditure', claimId],
    queryFn: async () => {
      const res = await customInstance<{ data: ExpenditureData }>({
        url: `/api/claims/${claimId}/expenditure`,
        method: 'GET',
      });
      return res.data;
    },
  });

  const claim = claimQuery.data?.data;

  // One evidence log per project that CONTRIBUTES to the claim's expenditure. The expenditure
  // route (routes/claims.ts) sums every income-year project whose status is NOT 'no_rd' or
  // 'archived' (and not soft-deleted) — including 'completed'/'cancelled' projects closed
  // mid-year. We must mirror that predicate exactly, otherwise a closed project shows dollars in
  // "Claim totals" with no evidence section. The list route only accepts a single `status` param
  // (can't express "not X and not Y"), so we fetch by income year (the route already excludes
  // soft-deleted rows) and exclude the same statuses client-side. Only enabled once the claim
  // (and therefore its incomeYear) has loaded.
  const projectsQueryParams = { incomeYear: claim?.incomeYear ?? undefined };
  const projectsQuery = useGetProjects(projectsQueryParams, {
    query: { queryKey: getGetProjectsQueryKey(projectsQueryParams), enabled: !!claim?.incomeYear },
  });
  const EXCLUDED_EVIDENCE_STATUSES = new Set(['no_rd', 'archived']);
  const evidenceProjects =
    projectsQuery.data?.data?.items?.filter((p) => !EXCLUDED_EVIDENCE_STATUSES.has(p.status)) ?? [];

  // Full-page loading / error gate on the PRIMARY claim query only. Readiness and expenditure
  // render with their own inline fallbacks so a transient 500 in one doesn't blank the page.
  if (claimQuery.isLoading) {
    return (
      <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="claim-readiness-summary-loading">
        <Skeleton className="h-8 w-64" />
        <SkeletonText lines={2} />
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (claimQuery.error || !claim) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <InlineError
          message="Failed to load the claim readiness summary. Please try again."
          onRetry={() => void claimQuery.refetch()}
        />
      </div>
    );
  }

  const checks = readinessQuery.data?.checks ?? [];
  const blockingCount = readinessQuery.data?.blockingCount ?? checks.filter((c) => c.status === 'blocking').length;
  const warningCount = checks.filter((c) => c.status === 'warning').length;

  const labourTotal = expenditureQuery.data?.labour?.totalRdExpenditure ?? 0;
  const nonLabourTotal = expenditureQuery.data?.nonLabour?.totalNonLabourExpenditure ?? 0;
  const grandTotal = expenditureQuery.data?.totalExpenditure ?? 0;

  const statusMeta = claim.status ? CLAIM_STATUS_META[claim.status] : undefined;

  const generatedAt = new Date().toLocaleString('en-AU', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  return (
    <div id="readiness-summary-doc" className="max-w-3xl mx-auto p-6 space-y-6">
      <style>{PRINT_STYLE}</style>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-ink">{claim.name}</h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span>{claim.incomeYear ? formatFy(claim.incomeYear) : '—'}</span>
            <span aria-hidden="true">·</span>
            {statusMeta ? (
              <Badge variant={statusMeta.variant}>
                {statusMeta.icon}
                {statusMeta.label}
              </Badge>
            ) : (
              <span className="capitalize">{claim.status ?? '—'}</span>
            )}
            <span aria-hidden="true">·</span>
            <ClaimModeBadge mode={expenditureQuery.data?.mode} locked={claim.status === 'locked'} />
            <span aria-hidden="true">·</span>
            <span>Generated {generatedAt}</span>
          </p>
        </div>
        <Button variant="secondary" className="no-print" onClick={() => window.print()}>
          <Printer className="h-4 w-4" aria-hidden="true" />
          Print / Save as PDF
        </Button>
      </div>

      {/* Readiness section */}
      <section className="space-y-4 summary-block">
        {readinessQuery.isLoading ? (
          <div className="space-y-2">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : readinessQuery.error ? (
          <InlineError
            message="Failed to load the readiness checklist."
            onRetry={() => void readinessQuery.refetch()}
          />
        ) : (
          <>
            {/* Blocker / warning breakdown */}
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="flex items-center gap-1.5">
                <XCircle className="h-4 w-4 text-destructive" aria-hidden="true" />
                {blockingCount} blocking issue{blockingCount === 1 ? '' : 's'}
              </span>
              <span className="flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden="true" />
                {warningCount} warning{warningCount === 1 ? '' : 's'}
              </span>
            </div>

            <Card variant="static" className="p-0">
              {checks.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-muted-foreground">
                  <FileWarning className="h-5 w-5" aria-hidden="true" />
                  No readiness checks available yet.
                </div>
              ) : (
                <div className="divide-y divide-border/40 px-2">
                  {checks.map((check) => (
                    <ReadinessRow
                      key={check.code}
                      state={check.state}
                      icon={STATUS_ICON[check.status]}
                      name={check.message}
                      detail={check.detail}
                    />
                  ))}
                </div>
              )}
            </Card>
          </>
        )}
      </section>

      {/* Claim totals */}
      <Card variant="static" className="p-5 space-y-4 summary-block">
        <h2 className="font-medium text-sm">Claim totals</h2>
        {expenditureQuery.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : expenditureQuery.error ? (
          <InlineError
            message="Failed to load claim totals."
            onRetry={() => void expenditureQuery.refetch()}
          />
        ) : (
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Labour</p>
              <p className="text-xl font-semibold font-mono tabular-nums">{formatCurrency(labourTotal)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Non-labour</p>
              <p className="text-xl font-semibold font-mono tabular-nums">{formatCurrency(nonLabourTotal)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Grand total</p>
              <p className="text-xl font-semibold font-mono tabular-nums text-primary">{formatCurrency(grandTotal)}</p>
            </div>
          </div>
        )}
      </Card>

      {/* Per-project evidence log — collapsed by default */}
      <section className="space-y-3 summary-block">
        <h2 className="font-medium text-sm">Evidence log</h2>
        {projectsQuery.isLoading ? (
          <Skeleton className="h-14 w-full" />
        ) : projectsQuery.error ? (
          <InlineError
            message="Failed to load projects for the evidence log."
            onRetry={() => void projectsQuery.refetch()}
          />
        ) : evidenceProjects.length ? (
          <div className="space-y-3">
            {evidenceProjects.map((project) => (
              <ProjectEvidenceSection key={project.id} projectId={project.id} projectName={project.name} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No R&amp;D projects for this income year.</p>
        )}
      </section>
    </div>
  );
}
